import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get authenticated user with timeout
        const authPromise = supabase.auth.getUser();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Auth timeout')), 10000)
        );
        
        const { data: { user }, error: authError } = await Promise.race([
          authPromise,
          timeoutPromise
        ]).catch(err => {
          // Handle timeout or network errors gracefully
          console.warn('Auth check failed:', err);
          return { data: { user: null }, error: err };
        });
        
        // If auth error but not session missing, log it
        if (authError && authError.message !== 'Auth session missing!') {
          console.error('Auth error:', authError);
        }
        
        if (!user) {
          if (isMounted) {
            setUserInfo(null);
            setLoading(false);
          }
          return;
        }

        // Check if data exists in sessionStorage (cache)
        const cachedData = sessionStorage.getItem(`user_${user.id}`);
        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            // Check if cache is still valid (less than 5 minutes old)
            if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
              if (isMounted) {
                setUserInfo(parsed.data);
                setLoading(false);
              }
              return;
            }
          } catch (parseErr) {
            console.warn('Cache parse error:', parseErr);
            sessionStorage.removeItem(`user_${user.id}`);
          }
        }

        // Fetch from database with optimized query
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select('id, full_name, email, role, photo, job, summary, location, phone, skills, created_at, degree_prefix, degree_suffix')
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          throw profileError;
        }

        if (profile && isMounted) {
          setUserInfo(profile);
          // Cache the data
          try {
            sessionStorage.setItem(`user_${user.id}`, JSON.stringify({
              data: profile,
              timestamp: Date.now()
            }));
          } catch (cacheErr) {
            console.warn('Cache save error:', cacheErr);
          }
        }
      } catch (err) {
        // Only log real errors, not session missing
        if (err.message !== 'Auth session missing!' && err.message !== 'Auth timeout') {
          console.error('Error fetching user info:', err);
        }
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserInfo();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        fetchUserInfo();
      } else if (event === 'SIGNED_OUT') {
        setUserInfo(null);
        sessionStorage.clear();
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const refreshUserInfo = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Clear cache
      sessionStorage.removeItem(`user_${user.id}`);

      const { data: profile, error } = await supabase
        .from('users')
        .select('id, full_name, email, role, photo, job, summary, location, phone, skills, degree_prefix, degree_suffix')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (profile) {
        setUserInfo(profile);
        sessionStorage.setItem(`user_${user.id}`, JSON.stringify({
          data: profile,
          timestamp: Date.now()
        }));
      }
    } catch (err) {
      console.error('Error refreshing user info:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserContext.Provider value={{ userInfo, loading, error, refreshUserInfo }}>
      {children}
    </UserContext.Provider>
  );
};
